import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { type OfferTab } from "../../types";
import { type OfferBucketCounts } from "../../useOfferBucketCounts";
import { tabBuckets } from "../../utils";

export const OFFER_TAB_LABEL_KEYS: Record<OfferTab, string> = {
  aggregatorAction: "representativeOffers.tabs.needsAggregatorAction",
  buyerAction: "representativeOffers.tabs.needsBuyerAction",
  inProgress: "representativeOffers.tabs.inProgress",
  needsAction: "representativeOffers.tabs.needsAction",
  awaiting: "representativeOffers.tabs.inProgress",
  closed: "representativeOffers.tabs.closed",
  all: "representativeOffers.tabs.all",
};

export const REPRESENTATIVE_OFFER_TABS: OfferTab[] = ["needsAction", "awaiting", "closed", "all"];

export const MANAGER_OFFER_TABS: OfferTab[] = [
  "aggregatorAction",
  "buyerAction",
  "inProgress",
  "closed",
  "all",
];

interface OfferTabsProps {
  tabs: OfferTab[];
  value: OfferTab;
  onChange: (value: OfferTab) => void;
  counts: OfferBucketCounts;
  isLoading: boolean;
}

/** Each offer is in one bucket only, so the tabs of either set add up to All. */
export const tabCount = (tab: OfferTab, counts: OfferBucketCounts): number =>
  tabBuckets(tab).reduce((total, bucket) => total + (counts[bucket] ?? 0), 0);

export const OfferTabs = ({ tabs, value, onChange, counts, isLoading }: OfferTabsProps) => {
  const { t } = useTranslation();

  return (
    <Tabs value={value} onValueChange={next => onChange(next as OfferTab)}>
      <TabsList
        aria-label={t("representativeOffers.tabs.ariaLabel")}
        className="max-w-full gap-1 overflow-x-auto rounded-xl p-1 group-data-[orientation=horizontal]/tabs:h-auto"
      >
        {tabs.map(tab => (
          <TabsTrigger key={tab} value={tab} className="flex-none gap-2 rounded-lg px-4 py-2">
            {t(OFFER_TAB_LABEL_KEYS[tab])}
            {!isLoading && (
              <Badge variant={value === tab ? "default" : "secondary"} className="tabular-nums">
                {tabCount(tab, counts)}
              </Badge>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
